import { Fragment, useState } from "react";

import FoundryLink from "../../components/FoundryLink";
import Head from "next/head";
import { PageFadeWrapper } from "../../components/PageWrapper";
import ResponsiveImage from "../../components/ResponsiveImage";
import Routes from "../../routes";

// RISO ink colors offered for printing. These are physical ink colors, so we
// use their exact hex/rgb values rather than the brand theme palette. `label`
// is the color used for the swatch name (yellow falls back to gold so it stays
// legible on the paper background).
const PRINT_COLORS = [
  { name: "Scarlet", rgb: "246, 80, 88", label: "#f65058" },
  { name: "Blue", rgb: "0, 120, 191", label: "#0078bf" },
  { name: "Yellow", rgb: "247, 255, 0", label: "#ac936e" },
  { name: "Fluorescent Pink", rgb: "255, 72, 176", label: "#ff48b0" },
  { name: "Mint", rgb: "130, 216, 213", label: "#82d8d5" },
  { name: "Light Teal", rgb: "0, 157, 165", label: "#009da5" },
  { name: "Kelly Green", rgb: "103, 179, 70", label: "#67b346" },
];

// TODO: replace with the live "Request a Printing Quote" form/Notion URL.
const QUOTE_REQUEST_URL =
  "https://www.notion.so/13d4ad451f2f80f3af2ff5f3013f1aa3?pvs=25";

function ColorSwatches({ rgb }: { rgb: string }) {
  return (
    <div className="flex w-full gap-[10px] overflow-hidden py-[10px]">
      {Array.from({ length: 10 }, (_, i) => (
        <div
          key={i}
          className="h-10 flex-1 md:h-16"
          style={{ backgroundColor: `rgba(${rgb}, ${(i + 1) / 10})` }}
        />
      ))}
    </div>
  );
}

const SETUP_FEES = [
  {
    text: "1 color",
    nonprofit_price: "$10.00/hr",
    commercial_price: "$10.00/hr",
  },
  {
    text: "2 colors",
    nonprofit_price: "$10.00/hr",
    commercial_price: "$20.00/hr",
  }, {
    text: "3 colors",
    nonprofit_price: "$10.00/hr",
    commercial_price: "$30.00/hr",
  }, {
    text: "4 colors",
    nonprofit_price: "$10.00/hr",
    commercial_price: "$40.00/hr"
  }
]

// const PRINT_FEES = [
//   {
//     text: "1-2 colors",
//     nonprofit_single_price: "$1.00/page",
//     small_single_price: "$1.50/page",
//     medium_single_price: "$2.50/page",
//     large_single_price: "$3.50/page",
//     nonprofit_double_price: "$1.50/page",
//     small_double_price: "$2.50/page",
//     medium_double_price: "$3.50/page",
//     large_double_price: "$4.50/page"
//   },
//   {
//     text: "3-4 colors",
//     nonprofit_single_price: "$1.50/page",
//     small_single_price: "$2.50/page",
//     medium_single_price: "$3.50/page",
//     large_single_price: "$4.50/page",
//     nonprofit_double_price: "$2.50/page",
//     small_double_price: "$3.50/page",
//     medium_double_price: "$4.50/page",
//     large_double_price: "$5.50/page"
//   },
// ]

// const PRINT_FEES = [
//   {
//     text: "1-2 colors",
//     nonprofit_single_price: "$0.75/page",
//     small_single_price: "$1.00/page",
//     medium_single_price: "$2.00/page",
//     large_single_price: "$3.00/page",
//     nonprofit_double_price: "$1.25/page",
//     small_double_price: "$2.00/page",
//     medium_double_price: "$3.00/page",
//     large_double_price: "$4.00/page"
//   },
//   {
//     text: "3-4 colors",
//     nonprofit_single_price: "$1.50/page",
//     small_single_price: "$2.00/page",
//     medium_single_price: "$3.00/page",
//     large_single_price: "$4.00/page",
//     nonprofit_double_price: "$2.50/page",
//     small_double_price: "$3.00/page",
//     medium_double_price: "$4.00/page",
//     large_double_price: "$5.00/page"
//   },
// ]


const PRINT_FEES = [
  {
    text: "1-2 colors",
    nonprofit_single_price: "$0.50/page",
    small_single_price: "$1.00/page",
    medium_single_price: "$1.50/page",
    large_single_price: "$2.50/page",
    nonprofit_double_price: "$1.00/page",
    small_double_price: "$1.25/page",
    medium_double_price: "$2.50/page",
    large_double_price: "$3.50/page"
  },
  {
    text: "3-4 colors",
    nonprofit_single_price: "$1.00/page",
    small_single_price: "$1.50/page",
    medium_single_price: "$2.50/page",
    large_single_price: "$3.50/page",
    nonprofit_double_price: "$2.00/page",
    small_double_price: "$2.50/page",
    medium_double_price: "$3.50/page",
    large_double_price: "$4.50/page"
  },
]

// const PRINT_FEES = [
//   {
//     text: "1-2 colors",
//     nonprofit_single_price: "$0.50/page",
//     small_single_price: "$0.75/page",
//     medium_single_price: "$1.50/page",
//     large_single_price: "$2.50/page",
//     nonprofit_double_price: "$1.00/page",
//     small_double_price: "$1.25/page",
//     medium_double_price: "$2.50/page",
//     large_double_price: "$3.50/page"
//   },
//   {
//     text: "3-4 colors",
//     nonprofit_single_price: "$1.00/page",
//     small_single_price: "$1.25/page",
//     medium_single_price: "$2.25/page",
//     large_single_price: "$3.25/page",
//     nonprofit_double_price: "$1.75/page",
//     small_double_price: "$2.25/page",
//     medium_double_price: "$3.25/page",
//     large_double_price: "$4.25/page"
//   },
// ]

// Paper pricing tiers. `key` matches the `<key>_price` fields on each PAPER_FEES
// entry; `label` is the tab text shown to the user.
const PAPER_FEE_TYPES = [
  { key: "nonprofit", label: "Non-Profit" },
  { key: "small", label: "Standard" },
  { key: "medium", label: "Medium-sized companies (<= 200)" },
  { key: "large", label: "Large companies (> 200)" },
] as const;

const PAPER_FEES = [
  {
    use: "Greeting Cards",
    nonprofit_price: "$0.30/page",
    small_price: "$0.30/page",
    medium_price: "$0.40/page",
    large_price: "$0.50/page",
    weight: "65lb cover",
    size: '5" x 6 5/8" (A7)',
    description: "Ivory Foldover Cardstock",
    color: "#FFFFFF"
  },
  {
    use: "Flyers & Zines",
    nonprofit_price: "$0.05/page",
    small_price: "$0.05/page",
    medium_price: "$0.10/page",
    large_price: "$0.20/page",
    weight: "20lb text",
    size: '8.5" x 11" (Letter)',
    description: "Staples Standard Copy Paper",
    color: "#FFFFFF"
  },
  {
    use: "Flyers & Zines",
    nonprofit_price: "$0.20/page",
    small_price: "$0.20/page",
    medium_price: "$0.30/page",
    large_price: "$0.40/page",
    weight: "70lb text",
    size: '8.5" x 11" (Letter)',
    description: "French Paper Speckletone True White (Warm/Off-White)",
    color: "#fff5e8"
  },
  {
    use: 'Zine Covers',
    nonprofit_price: "$0.30/page",
    small_price: "$0.30/page",
    medium_price: "$0.40/page",
    large_price: "$0.50/page",
    weight: "100lb text",
    size: '8.5" x 11" (Letter)',
    description: "French Paper Smart White (Bright White)",
    color: "#FFFFFF"
  },
  {
    use: 'Prints',
    nonprofit_price: "$0.40/page",
    small_price: "$0.40/page",
    medium_price: "$0.50/page",
    large_price: "$0.60/page",
    weight: "80lb cover",
    size: '8.5" x 11" (Letter)',
    description: "French Paper Speckletone True White (Warm/Off-White)",
    color: "#fff5e8"
  },
  {
    use: 'Prints',
    nonprofit_price: "$0.40/page",
    small_price: "$0.40/page",
    medium_price: "$0.50/page",
    large_price: "$0.60/page",
    weight: "80lb cover",
    size: '8.5" x 11" (Letter)',
    description: "French Paper Speckletone Starch White (Off-White)",
    color: "#fff5e8"
  },
  {
    use: 'Prints',
    nonprofit_price: "$0.40/page",
    small_price: "$0.40/page",
    medium_price: "$0.50/page",
    large_price: "$0.60/page",
    weight: "80lb cover",
    size: '8.5" x 14" (Legal)',
    description: "Mohawk Superfine Eggshell Finish - White",
    color: "#FFFFFF",
  },
  {
    use: "Large Flyers & Zines",
    nonprofit_price: "$0.05/page",
    small_price: "$0.05/page",
    medium_price: "$0.10/page",
    large_price: "$0.20/page",
    weight: "20lb text",
    size: '11" x 17" (Tabloid)',
    description: "Staples Standard Copy Paper",
    color: "#FFFFFF"
  },
  {
    use: 'Prints',
    nonprofit_price: "$0.40/page",
    small_price: "$0.40/page",
    medium_price: "$0.50/page",
    large_price: "$0.60/page",
    weight: "67lb cover",
    size: '11" x 17" (Tabloid)',
    description: "Lettermark Bristol White (Bright White)",
    color: "#FFFFFF"
  },
]

// French Paper "Speckletone" is a recycled stock flecked with small dark
// specks. We approximate that texture on its swatch with an inline SVG of
// scattered dots (varied position, size, brown/gray tone, opacity) layered
// over the base paper color. Coordinates are on a 20x20 grid.
const SPECKLETONE_SPECKS: [number, number, number, string, number][] = [
  [2.5, 3.2, 0.5, "5c4a32", 0.5],
  [6.1, 1.8, 0.35, "7a7266", 0.45],
  [9.3, 4.1, 0.6, "5c4a32", 0.5],
  [13.2, 2.6, 0.4, "8a7455", 0.4],
  [16.8, 3.9, 0.55, "7a7266", 0.5],
  [4.2, 7.3, 0.45, "8a7455", 0.45],
  [11.1, 6.8, 0.5, "7a7266", 0.4],
  [17.9, 7.1, 0.35, "5c4a32", 0.5],
  [1.9, 10.2, 0.5, "8a7455", 0.45],
  [7.6, 9.9, 0.4, "7a7266", 0.4],
  [14.3, 10.6, 0.6, "5c4a32", 0.5],
  [10.2, 12.4, 0.35, "8a7455", 0.4],
  [3.4, 13.8, 0.55, "7a7266", 0.45],
  [18.1, 13.2, 0.5, "5c4a32", 0.5],
  [6.8, 15.6, 0.4, "8a7455", 0.4],
  [12.9, 16.1, 0.55, "7a7266", 0.5],
  [2.2, 17.4, 0.35, "8a7455", 0.4],
  [16.4, 17.8, 0.5, "5c4a32", 0.45],
  [9.1, 18.9, 0.4, "7a7266", 0.4],
];
const SPECKLETONE_TEXTURE = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20'%3E${SPECKLETONE_SPECKS.map(
  ([cx, cy, r, color, opacity]) =>
    `%3Ccircle cx='${cx}' cy='${cy}' r='${r}' fill='%23${color}' opacity='${opacity}'/%3E`
).join("")}%3C/svg%3E")`;

function Print() {
  const [paperFeeType, setPaperFeeType] = useState<
    (typeof PAPER_FEE_TYPES)[number]["key"]
  >("small");

  return (
    <>
      <Head>
        <title>Space Type - Print with us</title>
        <meta property="og:title" content="Space Type - Print with us" />
      </Head>
      <PageFadeWrapper>
        <div className="nav-spacer--padded mb-24">
          <section className="fade-in pt-12 md:pt-24 container">
            {/* Intro */}
            <div className="mb-24">
              <h1 className="felicette-Bold color-gold text-5xl md:text-6xl max-w-4xl lg:max-w-5xl">
                Space Type provides{" "}
                <span className="felicette-Bold color-fluoorange">
                  printing services
                </span>{" "}
                within the{" "}
                <span className="felicette-Bold color-blue">United States.</span>
              </h1>
              <p className="monochromic-semilight mt-10 max-w-4xl text-lg">
                Interested in printing your artwork with us? We provide reliable printing services in a limited set of colors — with a quick turnaround.
                We try to complete production on print orders within 2-3 business days of receiving invoice payment, and zine orders within 2-3 weeks.
              </p>
              <div className="mt-8 flex gap-4">
                <FoundryLink
                  href="#submit-a-request"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    document
                      .getElementById("submit-a-request")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="w-fit py-4 rounded-md px-8 border-2 border-gold bg-gold-hover color-gold color-secondary-hover transition"
                >
                  I'm ready!
                </FoundryLink>
                <FoundryLink
                  href="#available-colors"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    document
                      .getElementById("available-colors")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="w-fit py-4 rounded-md px-8 border-2 border-blue bg-blue opacity-80 hover:opacity-100 color-secondary transition"
                >
                  Learn more
                </FoundryLink>
              </div>
            </div>
          </section>

          <ResponsiveImage
            src="/img/color-matches-33.png"
            alt="Colors from zines"
            className="z-[110]"
          // sizes="2000px"
          ></ResponsiveImage>

          <section className="fade-in px-4 md:px-12">
            <div className="mt-24 lg:grid grid-cols-12 xl:grid-cols-2 gap-24 items-start" id="available-colors">
              {/* Available colors */}
              <div
                className="px-6 py-10 md:px-12 xl:px-16 md:py-16 xl:py-20 max-w-4xl mb-24 col-span-5 xl:col-span-1"
                style={{
                  // Long dashed stroke matching the Figma design (~2.5px gold). CSS
                  // `border-dashed` only renders short browser-default dashes, so we
                  // draw the stroke as an SVG background. Each edge is a separate line
                  // with `pathLength='100'` and a dash pattern that starts and ends
                  // with a dash, guaranteeing a dash in every corner. Vertical edges
                  // get 3 dashes ('20 20') and horizontal edges 2 dashes
                  // ('33.333 33.333'); at the box's ~1.6 aspect ratio these render at
                  // near-equal physical dash lengths so they look even.
                  backgroundImage:
                    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25'%3E%3Cg fill='none' stroke='%23ac936e' stroke-width='5'%3E%3Cline x1='0' y1='0' x2='100%25' y2='0' stroke-dasharray='33.333 33.333' pathLength='100'/%3E%3Cline x1='100%25' y1='0' x2='100%25' y2='100%25' stroke-dasharray='20 20' pathLength='100'/%3E%3Cline x1='100%25' y1='100%25' x2='0' y2='100%25' stroke-dasharray='33.333 33.333' pathLength='100'/%3E%3Cline x1='0' y1='100%25' x2='0' y2='0' stroke-dasharray='20 20' pathLength='100'/%3E%3C/g%3E%3C/svg%3E\")",
                }}
              >
                <h2 className="felicette-Bold color-gold text-3xl md:text-5xl mb-12">
                  Available Colors
                </h2>
                <div className="w-full mb-4">
                  {PRINT_COLORS.map((color) => (
                    <div key={color.name} className="flex w-full flex-col gap-2">
                      <ColorSwatches rgb={color.rgb} />
                      <p
                        className="felicette-Bold text-xl md:text-2xl margin mt-0 pt-0"
                        style={{ color: color.label }}
                      >
                        {color.name}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="monochromic-semilight color-gold max-w-4xl text-lg">
                  <p>
                    Note that digital color representations are only an
                    approximation of the physical result. In addition, the more
                    colors you mix together, the more &ldquo;soft&rdquo; and
                    &ldquo;fuzzy&rdquo; the texture will be on paper.
                  </p>
                  <p className="mt-6">
                    Our machine runs two drums at a time. We do our best to have
                    excellent registration, but 3-4 color prints may introduce
                    slight variations.
                  </p>
                </div>
              </div>

              <div className="col-span-7 xl:col-span-1" id="pricing">
                {/* Pricing */}
                <div className="flex flex-col gap-12 mb-12 items-start">
                  <div>
                    <h2 className="felicette-Bold color-gold text-3xl md:text-5xl">
                      Pricing
                    </h2>
                    <p className="mt-6 rounded bg-gold-80-transparent color-secondary py-2 px-4 self-start">
                      ✿ Pricing is subject to adjustment during our pilot phase ✿
                    </p>
                  </div>

                  <div>
                    <h3 className="felicette-Bold text-2xl md:text-3xl">
                      Setup Fees
                    </h3>
                    <div className="monochromic-semilight mt-6 mb-4 max-w-4xl text-lg">
                      <p>
                        Setup fees cover the cost of masters and our time spent reviewing your project,
                        checking and preparing files, and maintaining the RISO printer for operations.
                      </p>
                      <p className="mt-6">
                        We provide flat-rate reduced fees for non-profit projects (i.e. personal, community,
                        and social good initiatives.)
                      </p>
                      <p className="mt-6">
                        Every project assumes a minimum of 1 hour of labor wages for prints, and 2 hours for zines.
                        Expected time will be evaluated and quoted appropriately as
                        part of intake. Setup fees for multiple prints may be combined
                        to reduce overall pricing.
                      </p>
                    </div>

                    <table className="table-auto w-fit gap-4">
                      <thead>
                        <tr>
                          <th className="text-start">Color Count</th>
                          <th className="text-start pl-4">Non-Profit Cost</th>
                          <th className="text-start pl-4">Standard Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {SETUP_FEES.map((fee) =>
                          <tr>
                            <td className="text-start">
                              {fee.text}
                            </td>
                            <td
                              className="text-start pl-4"
                            >
                              {fee.nonprofit_price}
                            </td>
                            <td
                              className="text-start pl-4"
                            >
                              {fee.commercial_price}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div>
                    <h3 className="felicette-Bold text-2xl md:text-3xl">
                      Print Fees
                    </h3>
                    <p className="mt-6 max-w-4xl text-lg">
                      These fees apply for single-sheet prints. This includes printing operations + producing optional hard proofs for review before the final print run.
                    </p>
                    <div className="mt-6 mb-4 flex flex-row flex-wrap items-center gap-2">
                      {PAPER_FEE_TYPES.map((type) => (
                        <><button
                          key={type.key}
                          type="button"
                          onClick={() => setPaperFeeType(type.key)}
                          className={`mt-0 py-0 leading-none ${paperFeeType === type.key
                            ? "color-gold"
                            : ""
                            }`}
                        >
                          {type.label}
                        </button>
                          <span className="leading-none">|</span></>
                      ))}
                    </div>
                    <table className="table-auto w-fit gap-4">
                      <thead>
                        <tr>
                          <th className="text-start">Color Count</th>
                          <th className="text-start pl-4">Price (Single-sided)</th>
                          <th className="text-start pl-4">Price (Double-sided)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {PRINT_FEES.map((fee) =>
                          <tr>
                            <td className="text-start">
                              {fee.text}
                            </td>
                            <td
                              className="text-start pl-4"
                            >
                              {fee[`${paperFeeType}_single_price`]}
                            </td>
                            <td
                              className="text-start pl-4"
                            >
                              {fee[`${paperFeeType}_double_price`]}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div>
                    <h3 className="felicette-Bold text-2xl md:text-3xl">
                      Paper Fees
                    </h3>
                    <p className="mt-6 max-w-4xl text-lg">
                      We have the following paper in stock for the listed prices.
                      This covers fees for paper shipping, stocking, and the extra paper that is used for print
                      tests and quality control.
                    </p>
                    <p className="mt-6 max-w-4xl text-lg">
                      If you're using your own paper, it must be shipped or scheduled for drop off at our address.
                    </p>
                    <div className="mt-6 mb-4 flex flex-row flex-wrap items-center gap-2">
                      {PAPER_FEE_TYPES.map((type) => (
                        <><button
                          key={type.key}
                          type="button"
                          onClick={() => setPaperFeeType(type.key)}
                          className={`mt-0 py-0 leading-none ${paperFeeType === type.key
                            ? "color-gold"
                            : "color-neutral-300-hover"
                            }`}
                        >
                          {type.label}
                        </button>
                          <span className="leading-none">|</span></>
                      ))}
                    </div>
                    <table className="table-auto w-fit gap-4">
                      <thead>
                        <tr>
                          <th className="text-start">Price</th>
                          <th className="text-start pl-4">Weight</th>
                          <th className="text-start pl-4">Use</th>
                          <th className="text-start pl-4">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {PAPER_FEES.map((fee, idx) => {
                          const isNewSize =
                            idx === 0 || PAPER_FEES[idx - 1].size !== fee.size;
                          return (
                            <Fragment key={idx}>
                              {isNewSize && (
                                <tr className="bg-gold-80-transparent color-secondary">
                                  <td
                                    colSpan={4}
                                    className="pl-2 monochromic-semilight text-start pt-2 pb-2"
                                  >
                                    {fee.size}
                                  </td>
                                </tr>
                              )}
                              <tr>
                                <td className="text-start">
                                  {fee[`${paperFeeType}_price`]}
                                </td>
                                <td className="text-start pl-4">
                                  {fee.weight}
                                </td>
                                <td className="text-start pl-4">{fee.use}</td>
                                <td className="text-start pl-4">
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="h-5 w-5 flex-none border"
                                      style={{
                                        backgroundColor: fee.color,
                                        backgroundImage:
                                          fee.description.includes("Speckletone")
                                            ? SPECKLETONE_TEXTURE
                                            : undefined,
                                        borderColor: "rgba(0, 0, 0, 0.25)",
                                        zIndex: 100
                                      }}
                                    />
                                    <span>{fee.description}</span>
                                  </div>
                                </td>
                              </tr>
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div>
                    <h3 className="felicette-Bold text-2xl md:text-3xl">
                      Staple-bound zines
                    </h3>
                    <p className="mt-6 max-w-4xl text-lg">
                      We provide full end-to-end services for printing and binding
                      zines. For more information about pricing, submit a request below
                      with information about your project.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit a request */}
            <div className="flex lg:justify-center lg:mt-12">
              <div
                className="flex flex-col gap-12 w-full max-w-4xl lg:px-16 lg:py-16 xl:py-20 lg:border-2 border-gold"
                id="submit-a-request"
              >
                <div>
                  <h2 className="felicette-Bold color-gold text-3xl md:text-5xl">
                    Submit a request
                  </h2>
                  <div className="monochromic-semilight mt-6 max-w-4xl text-lg">
                    <p>
                      Send us files to print! We try to complete production on print orders within
                      2-3 business days of receiving invoice payment, and zine orders within
                      2-3 weeks.
                    </p>
                    <p className="mt-6 mb-0">
                      We expect files to be print-ready and ask that you send them
                      several days in advance so we can review them and provide
                      recommendations. Color separation services will incur additional charges and we cannot guarantee color accuracy.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="felicette-Bold text-2xl md:text-3xl">
                    Quoting and Invoicing
                  </h3>
                  <div className="monochromic-semilight mt-6 max-w-4xl text-lg">
                    <p>
                      Before printing your project, we will ask you to provide
                      information so that we can confirm a quote and time
                      estimation. This will ensure that we can provide you with our
                      highest quality of work at the lowest costs, and allows us to
                      evaluate projects that don&rsquo;t fit neatly into the standard
                      pricing structure.
                    </p>
                    <p className="mt-6">
                      Once a final quote has been reviewed and agreed upon, we will
                      send you an invoice that finalizes the details of your
                      project. Please review the invoice carefully before paying.
                    </p>
                    <p className="mt-6 mb-0">
                      Invoices must be paid in full before production begins.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="felicette-Bold text-2xl md:text-3xl">
                    I&rsquo;m ready!~
                  </h3>
                  <p className="max-w-4xl text-lg">
                    By submitting a print request, you agree to our <FoundryLink className="underline color-blue-hover" href={Routes.printToc}>Terms and Conditions</FoundryLink>. We'll get back to you within 1-3 days depending on project complexity!
                  </p>
                  <FoundryLink
                    href={QUOTE_REQUEST_URL}
                    target="_blank"
                    className="mt-8 inline-block w-fit py-4 rounded-md px-8 border-2 border-gold bg-gold-hover color-gold color-secondary-hover transition"
                  >
                    Request a Printing Quote
                  </FoundryLink>
                </div>
              </div>
            </div>
          </section>
        </div>
      </PageFadeWrapper>
    </>
  );
}

Print.displayName = "Print";
export default Print;
